/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { Platform } from 'react-native';
import type { Router } from 'expo-router';
import {
  HOTEL_SEARCH_DEFAULTS,
  HOTEL_SEARCH_URL_CHANGE_EVENT,
} from '../configs/constants';

/**
 * Selected-hotel query keys that must always be actively cleared when the
 * next list search URL omits them. These can be set by call sites (e.g. the
 * map view's native router.setParams fallback) that update expo-router's
 * internal route params without also updating window.location.search, so a
 * diff against the visible URL alone would miss them.
 */
const SELECTED_HOTEL_QUERY_KEYS_TO_CLEAR = [
  'aaId',
  'user_selected_hotel_id',
  'user_selected_id',
  'destination_name',
];

const HOTEL_SEARCH_ATTRIBUTION_KEYS = ['channel', 'agent'] as const;

/**
 * Web-only: copy channel/agent from the live address bar into a rebuilt
 * edit-search URL. Absent keys are not invented; generated values are replaced
 * by the live URL so stale attribution cannot linger.
 */
export const preserveHotelSearchAttribution = (url: string): string => {
  if (
    Platform.OS !== 'web' ||
    typeof window === 'undefined' ||
    typeof window.location?.search !== 'string'
  ) {
    return url;
  }

  const currentParams = new URLSearchParams(window.location.search);
  const targetUrl = new URL(url, window.location.origin || 'http://localhost');
  if (
    !HOTEL_SEARCH_ATTRIBUTION_KEYS.some(
      (key) => currentParams.has(key) || targetUrl.searchParams.has(key),
    )
  ) {
    return url;
  }

  HOTEL_SEARCH_ATTRIBUTION_KEYS.forEach((key) => {
    targetUrl.searchParams.delete(key);
    currentParams.getAll(key).forEach((value) => {
      targetUrl.searchParams.append(key, value);
    });
  });

  return `${url.split(/[?#]/)[0]}${targetUrl.search}${targetUrl.hash}`;
};

/**
 * Notifies listeners (e.g. list/filter widgets) that the hotel search URL
 * changed via an in-place update that does not remount the screen.
 */
export const dispatchHotelSearchUrlChange = (): void => {
  if (
    typeof window !== 'undefined' &&
    typeof window.dispatchEvent === 'function' &&
    typeof Event === 'function'
  ) {
    window.dispatchEvent(new Event(HOTEL_SEARCH_URL_CHANGE_EVENT));
  }
};

/**
 * Web-only: rewrite the `currency` param of the URL currently in the address bar.
 *
 * Screens restored from a back navigation still carry the currency that was
 * active when their history entry was created. Once the user changes currency
 * elsewhere (e.g. from the details map) that entry is stale, so every screen that
 * can be returned to has to reconcile its URL with the global currency on mount.
 *
 * Pass `replaceListSearchParams` where it is available: a bare `replaceState`
 * leaves the web router holding the previous URL, which it re-asserts on its next
 * render and makes the currency toggle between the old and new value.
 */
export const syncCurrencyToHotelSearchUrl = (
  currencyCode: string,
  replaceListSearchParams?: (url: string) => void,
): void => {
  if (Platform.OS !== 'web' || typeof window === 'undefined') {
    return;
  }

  const origin = window.location.origin || 'http://localhost';
  const pathname = window.location.pathname || '/';
  const search = window.location.search || '';
  const hash = window.location.hash || '';
  const nextUrl = new URL(
    window.location.href || `${pathname}${search}${hash}`,
    origin,
  );

  const currentUrlCurrency = nextUrl.searchParams
    .get('currency')
    ?.toUpperCase();
  if (currentUrlCurrency === currencyCode) {
    return;
  }

  // A URL that never carried the param keeps its clean form on the default
  // currency — do not start stamping it in.
  if (!currentUrlCurrency && currencyCode === HOTEL_SEARCH_DEFAULTS.CURRENCY) {
    return;
  }

  nextUrl.searchParams.set('currency', currencyCode);
  window.history.replaceState(window.history.state, '', nextUrl.toString());
  replaceListSearchParams?.(`${nextUrl.pathname}${nextUrl.search}`);

  dispatchHotelSearchUrlChange();
};

/**
 * Web-only: update the current list URL's query params in place using
 * history.replaceState (no navigation/stack change), syncing expo-router params
 * and clearing any stale key the new URL doesn't carry. Centralized so the
 * navigation context and the standalone navigation hook share one implementation.
 */
export const replaceHotelListSearchUrl = (
  targetUrl: URL,
  router: Pick<Router, 'setParams'>,
): void => {
  const searchParams: Record<string, string | string[] | undefined> =
    Object.fromEntries(targetUrl.searchParams.entries());

  HOTEL_SEARCH_ATTRIBUTION_KEYS.forEach((key) => {
    const values = targetUrl.searchParams.getAll(key);
    if (values.length > 1) {
      searchParams[key] = values;
    }
  });

  // router.setParams() shallow-merges with the existing route params instead
  // of replacing them, so any key present on the URL we're navigating away
  // from but absent from targetUrl would otherwise linger (e.g. a stale
  // checkin/checkout/location_id surviving alongside a freshly-built
  // fromDate/toDate/destination). Explicitly null out every such key, plus
  // the always-cleared selected-hotel keys above, so the new search criteria
  // fully replaces the old one.
  const previousSearchParams =
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search)
      : undefined;
  const staleKeys = new Set(SELECTED_HOTEL_QUERY_KEYS_TO_CLEAR);
  previousSearchParams?.forEach((_value, key) => staleKeys.add(key));

  staleKeys.forEach((key) => {
    if (!targetUrl.searchParams.has(key)) {
      searchParams[key] = undefined;
    }
  });

  if (router && typeof router.setParams === 'function') {
    router.setParams(searchParams);
  }

  if (typeof window !== 'undefined') {
    window.history.replaceState(window.history.state, '', targetUrl.toString());
  }

  dispatchHotelSearchUrlChange();
};
